import arrowLeftIcon from "@loot-os/icons/icons/arrow-left";
import plusIcon from "@loot-os/icons/icons/plus";
import searchIcon from "@loot-os/icons/icons/search";
import { type FormEvent, lazy, type ReactNode, Suspense, useState } from "react";
import { Icon } from "../../core/Icon.js";

import { Input } from "../../core/Input.js";
import { BalancePill } from "../../domain/BalancePill.js";
import { AppLink } from "../../foundations/AppLink.js";
import { cn } from "../../foundations/cn.js";
import { FOCUS_RING } from "../../foundations/interaction.js";
import { useLayout } from "../../foundations/themeContext.js";
import { Footer } from "../../patterns/Footer.js";
import { Logo } from "../../patterns/Logo.js";
import { MobileTabBar, MobileTopBar } from "../../patterns/MobileBars.js";
import { PageShell } from "../../patterns/PageShell.js";
import { TopNav } from "../../patterns/TopNav.js";
import { ActionButton } from "./ActionButton.js";
import { type ChromeHints, ChromeHintsContext } from "./chromeHints.js";
import type { StoreChrome as StoreChromeContent } from "./content.js";

/**
 * The footer's pack tear, its WebGL included, in a chunk of its own: only a store whose theme turns
 * the tear on (`layout.footer: "tear"`) downloads it.
 */
const PackTear = lazy(() =>
  import("../../moments/PackTear.js").then((module) => ({ default: module.PackTear })),
);

export interface StoreChromeProps {
  chrome: StoreChromeContent;
  /** The page opens on a stage that runs under a floating nav. Overrides the page's hint. */
  underNav?: boolean;
  /** An inner page: the mobile bar shows Back and the parent's name. Overrides the page's hint. */
  back?: { label: string; href: string };
  /** A commerce page's sticky action on phones, in place of the tab bar. */
  mobileAction?: ReactNode;
  children: ReactNode;
}

/**
 * The store's frame: both navigations, money and the footer. Mount it once around the app's
 * pages (it stays while they change); each page tells it what it needs with `useChromeHints`.
 */
export function StoreChrome({ chrome, underNav, back, mobileAction, children }: StoreChromeProps) {
  const [hints, setHints] = useState<ChromeHints>({});
  const layout = useLayout();
  const backLink = back ?? hints.back;
  const onSearch = chrome.onSearch;
  const search = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = new FormData(event.currentTarget).get("q");
    onSearch?.(typeof query === "string" ? query.trim() : "");
  };
  const money =
    chrome.balance !== undefined ? (
      <>
        <BalancePill cents={chrome.balance} onClick={chrome.onBalance} />
        {chrome.deposit && <ActionButton action={chrome.deposit} size="sm" shine />}
      </>
    ) : (
      chrome.signIn && <ActionButton action={chrome.signIn} size="sm" />
    );
  const actions = (
    <>
      {money}
      {chrome.actions}
    </>
  );
  return (
    <PageShell
      underNav={underNav ?? hints.underNav}
      nav={
        <TopNav
          logo={<Logo name={chrome.name} src={chrome.logoSrc} markOnly="below-lg" />}
          links={chrome.links}
          search={
            // biome-ignore lint/a11y/useSemanticElements: <search> is too new for every browser a store serves.
            <form role="search" onSubmit={search}>
              <Input
                label={chrome.searchLabel}
                hideLabel
                icon={searchIcon}
                size="sm"
                type="search"
                name="q"
                placeholder={chrome.searchLabel}
              />
            </form>
          }
          actions={actions}
        />
      }
      mobileTop={
        <MobileTopBar
          start={
            backLink ? (
              <AppLink
                href={backLink.href}
                className={cn(
                  "flex items-center gap-1 rounded-xs font-body text-body-b1-strong text-text-primary",
                  FOCUS_RING,
                )}
              >
                <Icon icon={arrowLeftIcon} size={18} />
                {backLink.label}
              </AppLink>
            ) : (
              <Logo name={chrome.name} src={chrome.logoSrc} markOnly={Boolean(chrome.logoSrc)} />
            )
          }
          end={
            <>
              {chrome.balance !== undefined ? (
                <>
                  <BalancePill cents={chrome.balance} onClick={chrome.onBalance} />
                  {chrome.deposit && (
                    <ActionButton action={chrome.deposit} iconOnly={plusIcon} size="sm" />
                  )}
                </>
              ) : (
                chrome.signIn && <ActionButton action={chrome.signIn} size="sm" />
              )}
              {chrome.actions}
            </>
          }
        />
      }
      mobileTabs={
        mobileAction ?? (hints.hideTabs ? undefined : <MobileTabBar tabs={chrome.mobileTabs} />)
      }
      footer={
        <Footer
          logo={<Logo name={chrome.name} src={chrome.logoSrc} />}
          lead={
            layout.footer === "tear" &&
            chrome.footerTear && (
              // Its own height while it loads, so the footer never jumps when it lands.
              <Suspense fallback={<div aria-hidden className="h-16 lg:h-19" />}>
                <PackTear
                  name={chrome.name}
                  markSrc={chrome.logoSrc}
                  pulls={chrome.footerTear.pulls}
                  words={chrome.footerTear.words}
                  action={<ActionButton action={chrome.footerTear.action} size="sm" shine />}
                />
              </Suspense>
            )
          }
          {...chrome.footer}
        />
      }
    >
      <ChromeHintsContext.Provider value={setHints}>{children}</ChromeHintsContext.Provider>
    </PageShell>
  );
}
